import { useCallback, useEffect, useState } from 'react';
import { Info, X } from 'lucide-react';

interface WholesaleSurchargeNoticeProps {
  /** Surcharge rate applied to the wholesale part of the cart. */
  percent: number;
}

/**
 * Explains why paying with Mercado Pago costs more when the cart has wholesale
 * prices. The short version sits inline next to the payment methods; the full
 * reasoning opens in a dialog so the checkout stays uncluttered.
 */
export function WholesaleSurchargeNotice({ percent }: WholesaleSurchargeNoticeProps) {
  const [isOpen, setIsOpen] = useState(false);

  const close = useCallback(() => setIsOpen(false), []);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  }, [close]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  if (percent <= 0) return null;

  return (
    <>
      <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-[13px] text-amber-900">
        Tu pedido contiene productos con precio mayorista. Abonando con Mercado Pago,
        estos productos tienen un recargo del {percent}%.
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="ml-1 inline-flex items-center gap-1 font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
        >
          <Info className="h-3.5 w-3.5" />
          ¿Por qué?
        </button>
      </div>

      {isOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-[fadeIn_150ms_ease-out]"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={close}
        >
          <div
            className="w-full max-w-[440px] rounded-xl bg-white p-6 shadow-xl animate-[slideUp_200ms_ease-out] sm:p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="wholesale-surcharge-title"
          >
            <div className="mb-4 flex items-start justify-between gap-4">
              <h2
                id="wholesale-surcharge-title"
                className="text-lg font-semibold text-gray-800"
              >
                ¿Por qué Mercado Pago tiene un costo adicional?
              </h2>
              <button
                type="button"
                onClick={close}
                aria-label="Cerrar"
                className="-mr-1 -mt-1 rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex flex-col gap-3 text-[15px] leading-relaxed text-gray-600">
              <p>
                El recargo se aplica{' '}
                <strong className="font-semibold text-gray-800">
                  únicamente sobre los productos con precio mayorista
                </strong>{' '}
                de tu pedido, no sobre el total. El resto de los productos no
                tiene ningún adicional.
              </p>
              <p>
                Los precios mayoristas están calculados con márgenes mínimos para
                ofrecerte el mejor precio posible, así que ese{' '}
                <strong className="font-semibold text-gray-800">{percent}%</strong>{' '}
                cubre el costo del servicio de cobro de Mercado Pago.
              </p>
              <p>
                Si elegís <strong className="font-semibold text-gray-800">transferencia
                bancaria</strong>, mantenés el precio mayorista publicado{' '}
                <strong className="font-semibold text-gray-800">sin ningún adicional</strong>.
              </p>
            </div>

            <button
              type="button"
              onClick={close}
              className="mt-6 inline-flex h-11 w-full items-center justify-center rounded-lg bg-gray-800 text-[15px] font-semibold text-white transition-colors hover:bg-gray-900"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </>
  );
}
